// src/pages/Admin/AdminWarrantyRegistration.jsx
import React, { useState, useEffect } from "react";
import { adminService } from "../../services/admin.service";
import "./AdminWarrantyRegistration.css";

const AdminWarrantyRegistration = () => {
  const [formData, setFormData] = useState({
    serial_number: "",
    product_id: "",
    product_name: "",
    sku: "",
    purchase_date: "",
    warranty_start_date: "",
    warranty_end_date: "",
  });

  // State for Auto-suggest
  const [allProducts, setAllProducts] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  // Fetch products when component mounts
  useEffect(() => {
    const fetchCatalog = async () => {
      try {
        const response = await adminService.getProducts();
        // Adjust this depending on how your API returns the array.
        // Based on your controller, it's inside `response.products`
        setAllProducts(response.products || []);
      } catch (err) {
        console.error("Failed to load products for autocomplete", err);
      }
    };
    fetchCatalog();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const updated = { ...prev, [name]: value };

      // Auto-fill start date if purchase date is selected
      if (name === "purchase_date" && !prev.warranty_start_date) {
        updated.warranty_start_date = value;
      }
      return updated;
    });
  };

  // Dedicated handler for SKU to trigger the suggestions
  const handleSkuChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, sku: value }));

    if (value.trim().length > 0) {
      // Filter products by SKU or Name
      const filtered = allProducts.filter(
        (p) =>
          (p.sku && p.sku.toLowerCase().includes(value.toLowerCase())) ||
          (p.name && p.name.toLowerCase().includes(value.toLowerCase())),
      );
      setSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  // When a user clicks a suggestion from the dropdown
  const selectProduct = (product) => {
    setFormData((prev) => ({
      ...prev,
      sku: product.sku || "",
      product_name: product.name || "",
      product_id: product.product_id || "",
    }));
    setShowSuggestions(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    setError(null);

    // Extra frontend validation
    if (!formData.product_id) {
      setError(
        "Product ID is missing. Please select a valid SKU from the dropdown.",
      );
      setLoading(false);
      return;
    }

    try {
      await adminService.registerWarranty(formData);

      setMessage(
        `Warranty for ${formData.product_name} registered successfully!`,
      );

      setFormData({
        serial_number: "",
        product_id: "",
        product_name: "",
        sku: "",
        purchase_date: "",
        warranty_start_date: "",
        warranty_end_date: "",
      });
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to register warranty",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-warranty-page">
      <div className="admin-warranty-header">
        <h2>Register New Product Warranty</h2>
        <p>
          Search by SKU to auto-fill product details, then enter the serial
          number to activate.
        </p>
      </div>

      <div className="admin-warranty-card">
        {message && <div className="alert success">{message}</div>}
        {error && <div className="alert error">{error}</div>}

        <form onSubmit={handleSubmit} className="admin-warranty-form">
          <div className="form-row">
            <div className="form-group">
              <label>Serial Number (Required) *</label>
              <input
                type="text"
                name="serial_number"
                value={formData.serial_number}
                onChange={handleChange}
                placeholder="e.g. SN-BAT-102938"
                required
              />
            </div>

            {/* AUTOSUGGEST SKU FIELD */}
            <div className="form-group autocomplete-wrapper">
              <label>Search SKU (Required) *</label>
              <input
                type="text"
                name="sku"
                value={formData.sku}
                onChange={handleSkuChange}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)} // Delay hide so click registers
                placeholder="Start typing SKU or Product Name..."
                required
                autoComplete="off"
              />

              {/* DROPDOWN MENU */}
              {showSuggestions && suggestions.length > 0 && (
                <ul className="autocomplete-dropdown">
                  {suggestions.map((product) => (
                    <li
                      key={product.product_id}
                      onClick={() => selectProduct(product)}
                      className="dropdown-item"
                    >
                      <strong>{product.sku}</strong> - {product.name}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group full-width">
              <label>Product Name (Auto-filled) *</label>
              <input
                type="text"
                name="product_name"
                value={formData.product_name}
                readOnly // Made read-only so they don't mess up the linked data
                className="read-only-input"
                placeholder="Product name will appear here"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group full-width">
              <label>Product ID (Auto-filled) *</label>
              <input
                type="text"
                name="product_id"
                value={formData.product_id}
                readOnly // Made read-only
                className="read-only-input"
                placeholder="Product ID will appear here"
                required
              />
            </div>
          </div>

          <div className="form-row three-cols">
            <div className="form-group">
              <label>Purchase Date *</label>
              <input
                type="date"
                name="purchase_date"
                value={formData.purchase_date}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Warranty Start Date *</label>
              <input
                type="date"
                name="warranty_start_date"
                value={formData.warranty_start_date}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Warranty End Date *</label>
              <input
                type="date"
                name="warranty_end_date"
                value={formData.warranty_end_date}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? "Registering..." : "Register Warranty"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminWarrantyRegistration;
